import { goRequest, formGoClient } from '../utils/httpClientTs';
import type {
  StatusItemResponse,
  StatusPurchaseOrderResponse,
  CategoryResponse,
  GroupTypeResponse,
  ItemResponse,
  ItemDetailResponse,
  ItemListResponse,
  CreateItemRequest,
  UpdateItemRequest,
  ItemVendorsResponse,
  VendorResponse,
  VendorDetailResponse,
  VendorListResponse,
  CreateVendorRequest,
  UpdateVendorRequest,
  VendorItemResponse,
  CreateVendorItemRequest,
  UpdateVendorItemRequest,
  PurchaseOrderResponse,
  PurchaseOrderDetailResponse,
  PurchaseOrderListResponse,
  CreatePurchaseOrderRequest,
  UpdatePurchaseOrderRequest,
  ReceivePurchaseOrderRequest,
  InventoryResponse,
  InventoryListResponse,
  AdjustInventoryRequest,
  OnlineItemResponse,
  OnlineItemDetailResponse,
  OnlineItemListResponse,
  CreateOnlineItemRequest,
  UpdateOnlineItemRequest,
  AddOnlineItemImageRequest,
  UploadOnlineItemImageResponse,
} from '../types/chapterSupplies';

const BASE = '/api/chapter-supplies';

// --- Lookups ---

export const getItemStatuses = (): Promise<StatusItemResponse[]> =>
  goRequest<StatusItemResponse[]>({
    url: `${BASE}/statuses/items`,
    method: 'GET',
  });

export const getPurchaseOrderStatuses = (): Promise<
  StatusPurchaseOrderResponse[]
> =>
  goRequest<StatusPurchaseOrderResponse[]>({
    url: `${BASE}/statuses/purchase-orders`,
    method: 'GET',
  });

export const getCategories = (): Promise<CategoryResponse[]> =>
  goRequest<CategoryResponse[]>({ url: `${BASE}/categories`, method: 'GET' });

export const getGroupTypes = (): Promise<GroupTypeResponse[]> =>
  goRequest<GroupTypeResponse[]>({ url: `${BASE}/group-types`, method: 'GET' });

// --- Items ---

export interface ListItemsParams {
  limit?: number;
  offset?: number;
}

export const listItems = (
  params?: ListItemsParams
): Promise<ItemListResponse> => {
  const search = new URLSearchParams();
  if (params?.limit != null) search.set('limit', String(params.limit));
  if (params?.offset != null) search.set('offset', String(params.offset));
  const q = search.toString();
  return goRequest<ItemListResponse>({
    url: `${BASE}/items${q ? `?${q}` : ''}`,
    method: 'GET',
  });
};

export const getItem = (id: string): Promise<ItemDetailResponse> =>
  goRequest<ItemDetailResponse>({ url: `${BASE}/items/${id}`, method: 'GET' });

export const getItemVendors = (itemId: string): Promise<ItemVendorsResponse> =>
  goRequest<ItemVendorsResponse>({
    url: `${BASE}/items/${itemId}/vendors`,
    method: 'GET',
  });

export const createItem = (data: CreateItemRequest): Promise<ItemResponse> =>
  goRequest<ItemResponse>({ url: `${BASE}/items`, method: 'POST', data });

export const updateItem = (
  id: string,
  data: UpdateItemRequest
): Promise<ItemResponse> =>
  goRequest<ItemResponse>({
    url: `${BASE}/items/${id}`,
    method: 'PATCH',
    data,
  });

// --- Vendors ---

export const listVendors = (params?: {
  limit?: number;
  offset?: number;
}): Promise<VendorListResponse> => {
  const search = new URLSearchParams();
  if (params?.limit != null) search.set('limit', String(params.limit));
  if (params?.offset != null) search.set('offset', String(params.offset));
  const q = search.toString();
  return goRequest<VendorListResponse>({
    url: `${BASE}/vendors${q ? `?${q}` : ''}`,
    method: 'GET',
  });
};

export const getVendor = (id: string): Promise<VendorDetailResponse> =>
  goRequest<VendorDetailResponse>({
    url: `${BASE}/vendors/${id}`,
    method: 'GET',
  });

export const createVendor = (
  data: CreateVendorRequest
): Promise<VendorResponse> =>
  goRequest<VendorResponse>({ url: `${BASE}/vendors`, method: 'POST', data });

export const updateVendor = (
  id: string,
  data: UpdateVendorRequest
): Promise<VendorResponse> =>
  goRequest<VendorResponse>({
    url: `${BASE}/vendors/${id}`,
    method: 'PATCH',
    data,
  });

export const createVendorItem = (
  vendorId: string,
  data: CreateVendorItemRequest
): Promise<VendorItemResponse> =>
  goRequest<VendorItemResponse>({
    url: `${BASE}/vendors/${vendorId}/items`,
    method: 'POST',
    data,
  });

export const updateVendorItem = (
  vendorId: string,
  vendorItemId: string,
  data: UpdateVendorItemRequest
): Promise<VendorItemResponse> =>
  goRequest<VendorItemResponse>({
    url: `${BASE}/vendors/${vendorId}/items/${vendorItemId}`,
    method: 'PATCH',
    data,
  });

// --- Purchase orders ---

export const listPurchaseOrders = (params?: {
  limit?: number;
  offset?: number;
}): Promise<PurchaseOrderListResponse> => {
  const search = new URLSearchParams();
  if (params?.limit != null) search.set('limit', String(params.limit));
  if (params?.offset != null) search.set('offset', String(params.offset));
  const q = search.toString();
  return goRequest<PurchaseOrderListResponse>({
    url: `${BASE}/purchase-orders${q ? `?${q}` : ''}`,
    method: 'GET',
  });
};

export const getPurchaseOrder = (
  id: string
): Promise<PurchaseOrderDetailResponse> =>
  goRequest<PurchaseOrderDetailResponse>({
    url: `${BASE}/purchase-orders/${id}`,
    method: 'GET',
  });

export const createPurchaseOrder = (
  data: CreatePurchaseOrderRequest
): Promise<PurchaseOrderResponse> =>
  goRequest<PurchaseOrderResponse>({
    url: `${BASE}/purchase-orders`,
    method: 'POST',
    data,
  });

export const updatePurchaseOrder = (
  id: string,
  data: UpdatePurchaseOrderRequest
): Promise<PurchaseOrderResponse> =>
  goRequest<PurchaseOrderResponse>({
    url: `${BASE}/purchase-orders/${id}`,
    method: 'PATCH',
    data,
  });

export const receivePurchaseOrder = (
  id: string,
  data: ReceivePurchaseOrderRequest
): Promise<PurchaseOrderDetailResponse> =>
  goRequest<PurchaseOrderDetailResponse>({
    url: `${BASE}/purchase-orders/${id}/receive`,
    method: 'POST',
    data,
  });

// --- Inventory ---

export const listInventory = (params?: {
  limit?: number;
  offset?: number;
}): Promise<InventoryListResponse> => {
  const search = new URLSearchParams();
  if (params?.limit != null) search.set('limit', String(params.limit));
  if (params?.offset != null) search.set('offset', String(params.offset));
  const q = search.toString();
  return goRequest<InventoryListResponse>({
    url: `${BASE}/inventory${q ? `?${q}` : ''}`,
    method: 'GET',
  });
};

export const adjustInventory = (
  itemId: string,
  data: AdjustInventoryRequest
): Promise<InventoryResponse> =>
  goRequest<InventoryResponse>({
    url: `${BASE}/inventory/${itemId}`,
    method: 'PATCH',
    data,
  });

// --- Online items ---

export const listOnlineItems = (params?: {
  limit?: number;
  offset?: number;
}): Promise<OnlineItemListResponse> => {
  const search = new URLSearchParams();
  if (params?.limit != null) search.set('limit', String(params.limit));
  if (params?.offset != null) search.set('offset', String(params.offset));
  const q = search.toString();
  return goRequest<OnlineItemListResponse>({
    url: `${BASE}/online-items${q ? `?${q}` : ''}`,
    method: 'GET',
  });
};

export const getOnlineItem = (id: string): Promise<OnlineItemDetailResponse> =>
  goRequest<OnlineItemDetailResponse>({
    url: `${BASE}/online-items/${id}`,
    method: 'GET',
  });

export const createOnlineItem = (
  data: CreateOnlineItemRequest
): Promise<OnlineItemResponse> =>
  goRequest<OnlineItemResponse>({
    url: `${BASE}/online-items`,
    method: 'POST',
    data,
  });

export const updateOnlineItem = (
  id: string,
  data: UpdateOnlineItemRequest
): Promise<OnlineItemResponse> =>
  goRequest<OnlineItemResponse>({
    url: `${BASE}/online-items/${id}`,
    method: 'PATCH',
    data,
  });

export const uploadOnlineItemImage = (
  file: File
): Promise<UploadOnlineItemImageResponse> => {
  const form = new FormData();
  form.append('image', file);
  return formGoClient
    .post<UploadOnlineItemImageResponse>(
      `${BASE}/online-items/upload-image`,
      form
    )
    .then((res) => res.data);
};

export const addOnlineItemImage = (
  onlineItemId: string,
  data: AddOnlineItemImageRequest
): Promise<{ message: string }> =>
  goRequest<{ message: string }>({
    url: `${BASE}/online-items/${onlineItemId}/images`,
    method: 'POST',
    data,
  });

export const deleteOnlineItemImage = (
  onlineItemId: string,
  imageId: string
): Promise<{ message: string }> =>
  goRequest<{ message: string }>({
    url: `${BASE}/online-items/${onlineItemId}/images/${imageId}`,
    method: 'DELETE',
  });
